/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  Gender,
  IsoletteType,
  TransportReasonCategory,
  YesNoType,
} from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface RequestPatientProps {
  id: string;
  patientId: string | null;
  firstName: string | null;
  lastName: string | null;
  gender: Gender;
  dateOfBirth: Date | null;
  weightInKg: number | null;
  transportReasonId: string | null;
  notes: string | null;
  chiefComplaintNotes: string | null;
  transportReasonCategory: TransportReasonCategory | null;
  isIntubated: boolean | null;
  numberOfDrips: number | null;
  specialtyEquipment: string | null;
  rideAlongRequested: YesNoType | null;
  isoletteType: IsoletteType | null;
  isoletteNotes: string | null;
  isIsolette: boolean;
  transferReasonId: string | null;
  estimatedAgeInHours: number | null;
  gcs: number | null;
  bpSystolic: number | null;
  bpDiastolic: number | null;
  heartRate: number | null;
  oxygenSaturation: number | null;
  respiratoryRate: number | null;
  temperatureInCelsius: number | null;
  rideAlongWeightInKg: number | null;
  medicalRecordNumber: string | null;
  rideAlongName: string | null;
  rideAlongDateOfBirth: Date | null;
  requestId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class RequestPatientRecord extends Record<RequestPatientProps>({
  id: null as any,
  patientId: null as any,
  firstName: null as any,
  lastName: null as any,
  gender: null as any,
  dateOfBirth: null as any,
  weightInKg: null as any,
  transportReasonId: null as any,
  notes: null as any,
  chiefComplaintNotes: null as any,
  transportReasonCategory: null as any,
  isIntubated: null as any,
  numberOfDrips: null as any,
  specialtyEquipment: null as any,
  rideAlongRequested: null as any,
  isoletteType: null as any,
  isoletteNotes: null as any,
  isIsolette: null as any,
  transferReasonId: null as any,
  estimatedAgeInHours: null as any,
  gcs: null as any,
  bpSystolic: null as any,
  bpDiastolic: null as any,
  heartRate: null as any,
  oxygenSaturation: null as any,
  respiratoryRate: null as any,
  temperatureInCelsius: null as any,
  rideAlongWeightInKg: null as any,
  medicalRecordNumber: null as any,
  rideAlongName: null as any,
  rideAlongDateOfBirth: null as any,
  requestId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    patientId: string | null;
    firstName: string | null;
    lastName: string | null;
    gender: Gender;
    dateOfBirth: string | number | Date | null;
    weightInKg: number | null;
    transportReasonId: string | null;
    notes: string | null;
    chiefComplaintNotes: string | null;
    transportReasonCategory: TransportReasonCategory | null;
    isIntubated: boolean | null;
    numberOfDrips: number | null;
    specialtyEquipment: string | null;
    rideAlongRequested: YesNoType | null;
    isoletteType: IsoletteType | null;
    isoletteNotes: string | null;
    isIsolette: boolean;
    transferReasonId: string | null;
    estimatedAgeInHours: number | null;
    gcs: number | null;
    bpSystolic: number | null;
    bpDiastolic: number | null;
    heartRate: number | null;
    oxygenSaturation: number | null;
    respiratoryRate: number | null;
    temperatureInCelsius: number | null;
    rideAlongWeightInKg: number | null;
    medicalRecordNumber: string | null;
    rideAlongName: string | null;
    rideAlongDateOfBirth: string | number | Date | null;
    requestId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): RequestPatientRecord {
    if (json == null) {
      throw new Error('RequestPatientRecord.fromJS: parameter cannot be null');
    }
    return new RequestPatientRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'RequestPatientJson.id',
      ),
      patientId: json.patientId,
      firstName: json.firstName,
      lastName: json.lastName,
      gender: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'gender', Gender.Unspecified),
        'RequestPatientJson.gender',
      ),
      dateOfBirth: json.dateOfBirth != null ? new Date(json.dateOfBirth) : null,
      weightInKg: json.weightInKg,
      transportReasonId: json.transportReasonId,
      notes: json.notes,
      chiefComplaintNotes: json.chiefComplaintNotes,
      transportReasonCategory: json.transportReasonCategory,
      isIntubated: json.isIntubated,
      numberOfDrips: json.numberOfDrips,
      specialtyEquipment: json.specialtyEquipment,
      rideAlongRequested: json.rideAlongRequested,
      isoletteType: json.isoletteType,
      isoletteNotes: json.isoletteNotes,
      isIsolette: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isIsolette', false),
        'RequestPatientJson.isIsolette',
      ),
      transferReasonId: json.transferReasonId,
      estimatedAgeInHours: json.estimatedAgeInHours,
      gcs: json.gcs,
      bpSystolic: json.bpSystolic,
      bpDiastolic: json.bpDiastolic,
      heartRate: json.heartRate,
      oxygenSaturation: json.oxygenSaturation,
      respiratoryRate: json.respiratoryRate,
      temperatureInCelsius: json.temperatureInCelsius,
      rideAlongWeightInKg: json.rideAlongWeightInKg,
      medicalRecordNumber: json.medicalRecordNumber,
      rideAlongName: json.rideAlongName,
      rideAlongDateOfBirth:
        json.rideAlongDateOfBirth != null ? new Date(json.rideAlongDateOfBirth) : null,
      requestId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'requestId', '00000000-0000-0000-0000-000000000000'),
        'RequestPatientJson.requestId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'RequestPatientJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    patientId: string | null;
    firstName: string | null;
    lastName: string | null;
    gender: Gender;
    dateOfBirth: string | number | Date | null;
    weightInKg: number | null;
    transportReasonId: string | null;
    notes: string | null;
    chiefComplaintNotes: string | null;
    transportReasonCategory: TransportReasonCategory | null;
    isIntubated: boolean | null;
    numberOfDrips: number | null;
    specialtyEquipment: string | null;
    rideAlongRequested: YesNoType | null;
    isoletteType: IsoletteType | null;
    isoletteNotes: string | null;
    isIsolette: boolean;
    transferReasonId: string | null;
    estimatedAgeInHours: number | null;
    gcs: number | null;
    bpSystolic: number | null;
    bpDiastolic: number | null;
    heartRate: number | null;
    oxygenSaturation: number | null;
    respiratoryRate: number | null;
    temperatureInCelsius: number | null;
    rideAlongWeightInKg: number | null;
    medicalRecordNumber: string | null;
    rideAlongName: string | null;
    rideAlongDateOfBirth: string | number | Date | null;
    requestId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [RequestPatientRecord, Error[]] {
    if (json == null) {
      throw new Error('RequestPatientRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new RequestPatientRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'RequestPatientJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      patientId: tryOrDefault(
        () => json.patientId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      firstName: tryOrDefault(
        () => json.firstName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lastName: tryOrDefault(
        () => json.lastName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      gender: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'gender', Gender.Unspecified),
            'RequestPatientJson.gender',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateOfBirth: tryOrDefault(
        () => (json.dateOfBirth != null ? new Date(json.dateOfBirth) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      weightInKg: tryOrDefault(
        () => json.weightInKg,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transportReasonId: tryOrDefault(
        () => json.transportReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      chiefComplaintNotes: tryOrDefault(
        () => json.chiefComplaintNotes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transportReasonCategory: tryOrDefault(
        () => json.transportReasonCategory,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isIntubated: tryOrDefault(
        () => json.isIntubated,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      numberOfDrips: tryOrDefault(
        () => json.numberOfDrips,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      specialtyEquipment: tryOrDefault(
        () => json.specialtyEquipment,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rideAlongRequested: tryOrDefault(
        () => json.rideAlongRequested,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isoletteType: tryOrDefault(
        () => json.isoletteType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isoletteNotes: tryOrDefault(
        () => json.isoletteNotes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isIsolette: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isIsolette', false),
            'RequestPatientJson.isIsolette',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      transferReasonId: tryOrDefault(
        () => json.transferReasonId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      estimatedAgeInHours: tryOrDefault(
        () => json.estimatedAgeInHours,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      gcs: tryOrDefault(
        () => json.gcs,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      bpSystolic: tryOrDefault(
        () => json.bpSystolic,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      bpDiastolic: tryOrDefault(
        () => json.bpDiastolic,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      heartRate: tryOrDefault(
        () => json.heartRate,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      oxygenSaturation: tryOrDefault(
        () => json.oxygenSaturation,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      respiratoryRate: tryOrDefault(
        () => json.respiratoryRate,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      temperatureInCelsius: tryOrDefault(
        () => json.temperatureInCelsius,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rideAlongWeightInKg: tryOrDefault(
        () => json.rideAlongWeightInKg,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      medicalRecordNumber: tryOrDefault(
        () => json.medicalRecordNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rideAlongName: tryOrDefault(
        () => json.rideAlongName,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rideAlongDateOfBirth: tryOrDefault(
        () => (json.rideAlongDateOfBirth != null ? new Date(json.rideAlongDateOfBirth) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'requestId', '00000000-0000-0000-0000-000000000000'),
            'RequestPatientJson.requestId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'RequestPatientJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: RequestPatientProps) {
    super(props);
    if (props == null) {
      throw new Error('RequestPatientRecord constructor parameter cannot be null');
    }
  }
}

export abstract class RequestPatientViewModelBase implements RequestPatientProps {
  protected readonly _props: RequestPatientProps;
  private _json?: RequestPatientProps;

  get id(): string {
    return this._props.id;
  }
  get patientId(): string | null {
    return this._props.patientId;
  }
  get firstName(): string | null {
    return this._props.firstName;
  }
  get lastName(): string | null {
    return this._props.lastName;
  }
  get gender(): Gender {
    return this._props.gender;
  }
  get dateOfBirth(): Date | null {
    return this._props.dateOfBirth;
  }
  get weightInKg(): number | null {
    return this._props.weightInKg;
  }
  get transportReasonId(): string | null {
    return this._props.transportReasonId;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get chiefComplaintNotes(): string | null {
    return this._props.chiefComplaintNotes;
  }
  get transportReasonCategory(): TransportReasonCategory | null {
    return this._props.transportReasonCategory;
  }
  get isIntubated(): boolean | null {
    return this._props.isIntubated;
  }
  get numberOfDrips(): number | null {
    return this._props.numberOfDrips;
  }
  get specialtyEquipment(): string | null {
    return this._props.specialtyEquipment;
  }
  get rideAlongRequested(): YesNoType | null {
    return this._props.rideAlongRequested;
  }
  get isoletteType(): IsoletteType | null {
    return this._props.isoletteType;
  }
  get isoletteNotes(): string | null {
    return this._props.isoletteNotes;
  }
  get isIsolette(): boolean {
    return this._props.isIsolette;
  }
  get transferReasonId(): string | null {
    return this._props.transferReasonId;
  }
  get estimatedAgeInHours(): number | null {
    return this._props.estimatedAgeInHours;
  }
  get gcs(): number | null {
    return this._props.gcs;
  }
  get bpSystolic(): number | null {
    return this._props.bpSystolic;
  }
  get bpDiastolic(): number | null {
    return this._props.bpDiastolic;
  }
  get heartRate(): number | null {
    return this._props.heartRate;
  }
  get oxygenSaturation(): number | null {
    return this._props.oxygenSaturation;
  }
  get respiratoryRate(): number | null {
    return this._props.respiratoryRate;
  }
  get temperatureInCelsius(): number | null {
    return this._props.temperatureInCelsius;
  }
  get rideAlongWeightInKg(): number | null {
    return this._props.rideAlongWeightInKg;
  }
  get medicalRecordNumber(): string | null {
    return this._props.medicalRecordNumber;
  }
  get rideAlongName(): string | null {
    return this._props.rideAlongName;
  }
  get rideAlongDateOfBirth(): Date | null {
    return this._props.rideAlongDateOfBirth;
  }
  get requestId(): string {
    return this._props.requestId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isFemale() {
    return this._props.gender === Gender.Female;
  }
  public get isMale() {
    return this._props.gender === Gender.Male;
  }
  public get isOther() {
    return this._props.gender === Gender.Other;
  }
  public get isGenderUnspecified() {
    return this._props.gender === Gender.Unspecified;
  }
  public get isRideAlongRequestedUnspecified() {
    return this._props.rideAlongRequested === YesNoType.Unspecified;
  }
  public get isIsoletteTypeUnspecified() {
    return this._props.isoletteType === IsoletteType.Unspecified;
  }
  public get isEndocrine() {
    return this._props.transportReasonCategory === TransportReasonCategory.Endocrine;
  }
  public get isOB() {
    return this._props.transportReasonCategory === TransportReasonCategory.OB;
  }
  public get isSurgery() {
    return this._props.transportReasonCategory === TransportReasonCategory.Surgery;
  }
  public get isNo() {
    return this._props.rideAlongRequested === YesNoType.No;
  }
  public get isYes() {
    return this._props.rideAlongRequested === YesNoType.Yes;
  }
  public get isDraegerIsolette() {
    return this._props.isoletteType === IsoletteType.DraegerIsolette;
  }
  public get isLifeportDeck() {
    return this._props.isoletteType === IsoletteType.LifeportDeck;
  }
  public get isMetroPallet() {
    return this._props.isoletteType === IsoletteType.MetroPallet;
  }
  public get isSpectrumSled() {
    return this._props.isoletteType === IsoletteType.SpectrumSled;
  }
  public get isUnconfirmed() {
    return this._props.isoletteType === IsoletteType.Unconfirmed;
  }
  public get isVoyagerIsolette() {
    return this._props.isoletteType === IsoletteType.VoyagerIsolette;
  }
  public get isVoyagerIsolette1() {
    return this._props.isoletteType === IsoletteType.VoyagerIsolette1;
  }
  public get isVoyagerIsolette2() {
    return this._props.isoletteType === IsoletteType.VoyagerIsolette2;
  }
  public get isVoyagerIsolette4() {
    return this._props.isoletteType === IsoletteType.VoyagerIsolette4;
  }
  public get isVoyagerIsolette6() {
    return this._props.isoletteType === IsoletteType.VoyagerIsolette6;
  }

  public isAnyGender(...gender: Gender[]) {
    return gender.includes(this._props.gender);
  }
  public isAnyTransportReasonCategory(...transportReasonCategory: TransportReasonCategory[]) {
    return (
      this._props.transportReasonCategory != null &&
      transportReasonCategory.includes(this._props.transportReasonCategory)
    );
  }
  public isAnyRideAlongRequested(...yesNoType: YesNoType[]) {
    return (
      this._props.rideAlongRequested != null && yesNoType.includes(this._props.rideAlongRequested)
    );
  }
  public isAnyIsoletteType(...isoletteType: IsoletteType[]) {
    return this._props.isoletteType != null && isoletteType.includes(this._props.isoletteType);
  }

  constructor(props: RequestPatientProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        patientId: this._props.patientId,
        firstName: this._props.firstName,
        lastName: this._props.lastName,
        gender: this._props.gender,
        dateOfBirth: this._props.dateOfBirth,
        weightInKg: this._props.weightInKg,
        transportReasonId: this._props.transportReasonId,
        notes: this._props.notes,
        chiefComplaintNotes: this._props.chiefComplaintNotes,
        transportReasonCategory: this._props.transportReasonCategory,
        isIntubated: this._props.isIntubated,
        numberOfDrips: this._props.numberOfDrips,
        specialtyEquipment: this._props.specialtyEquipment,
        rideAlongRequested: this._props.rideAlongRequested,
        isoletteType: this._props.isoletteType,
        isoletteNotes: this._props.isoletteNotes,
        isIsolette: this._props.isIsolette,
        transferReasonId: this._props.transferReasonId,
        estimatedAgeInHours: this._props.estimatedAgeInHours,
        gcs: this._props.gcs,
        bpSystolic: this._props.bpSystolic,
        bpDiastolic: this._props.bpDiastolic,
        heartRate: this._props.heartRate,
        oxygenSaturation: this._props.oxygenSaturation,
        respiratoryRate: this._props.respiratoryRate,
        temperatureInCelsius: this._props.temperatureInCelsius,
        rideAlongWeightInKg: this._props.rideAlongWeightInKg,
        medicalRecordNumber: this._props.medicalRecordNumber,
        rideAlongName: this._props.rideAlongName,
        rideAlongDateOfBirth: this._props.rideAlongDateOfBirth,
        requestId: this._props.requestId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllRequestPatientEntities = (state: AppState) =>
  state.entityStores.requestPatient.entities;

export const selectRequestPatientById = (id: string) => (state: AppState) =>
  state.entityStores.requestPatient.entities.get(id);

export const selectRequestPatientMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.requestPatient.meta.get(id);

export const selectRequestPatientAndMetadataById = (id: string) =>
  createSelector(
    selectRequestPatientById(id),
    selectRequestPatientMetadataById(id),
    (requestPatient, meta) => ({ requestPatient, meta }),
  );

export function selectRequestPatientEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllRequestPatientEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, RequestPatientRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectRequestPatientEntitiesByPatientId(patientId: string) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.patientId === patientId).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByFirstName(firstName: string) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.firstName === firstName).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByLastName(lastName: string) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.lastName === lastName).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByGender(gender: Gender) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.gender === gender).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByDateOfBirth(dateOfBirth: Date) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.dateOfBirth === dateOfBirth).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByWeightInKg(weightInKg: number) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.weightInKg === weightInKg).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByTransportReasonId(transportReasonId: string) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.transportReasonId === transportReasonId).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByNotes(notes: string) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByChiefComplaintNotes(chiefComplaintNotes: string) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.chiefComplaintNotes === chiefComplaintNotes).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByTransportReasonCategory(
  transportReasonCategory: TransportReasonCategory,
) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.transportReasonCategory === transportReasonCategory).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByIsIntubated(isIntubated: boolean) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.isIntubated === isIntubated).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByNumberOfDrips(numberOfDrips: number) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.numberOfDrips === numberOfDrips).valueSeq(),
  );
}

export function selectRequestPatientEntitiesBySpecialtyEquipment(specialtyEquipment: string) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.specialtyEquipment === specialtyEquipment).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByRideAlongRequested(rideAlongRequested: YesNoType) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.rideAlongRequested === rideAlongRequested).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByIsoletteType(isoletteType: IsoletteType) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.isoletteType === isoletteType).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByIsoletteNotes(isoletteNotes: string) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.isoletteNotes === isoletteNotes).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByIsIsolette(isIsolette: boolean) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.isIsolette === isIsolette).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByTransferReasonId(transferReasonId: string) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.transferReasonId === transferReasonId).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByEstimatedAgeInHours(estimatedAgeInHours: number) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.estimatedAgeInHours === estimatedAgeInHours).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByGcs(gcs: number) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.gcs === gcs).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByBpSystolic(bpSystolic: number) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.bpSystolic === bpSystolic).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByBpDiastolic(bpDiastolic: number) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.bpDiastolic === bpDiastolic).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByHeartRate(heartRate: number) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.heartRate === heartRate).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByOxygenSaturation(oxygenSaturation: number) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.oxygenSaturation === oxygenSaturation).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByRespiratoryRate(respiratoryRate: number) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.respiratoryRate === respiratoryRate).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByTemperatureInCelsius(temperatureInCelsius: number) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.temperatureInCelsius === temperatureInCelsius).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByRideAlongWeightInKg(rideAlongWeightInKg: number) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.rideAlongWeightInKg === rideAlongWeightInKg).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByMedicalRecordNumber(medicalRecordNumber: string) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.medicalRecordNumber === medicalRecordNumber).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByRideAlongName(rideAlongName: string) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.rideAlongName === rideAlongName).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByRideAlongDateOfBirth(rideAlongDateOfBirth: Date) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.rideAlongDateOfBirth === rideAlongDateOfBirth).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByRequestId(requestId: string) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.requestId === requestId).valueSeq(),
  );
}

export function selectRequestPatientEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllRequestPatientEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
